<template>
  <div class="contact-dialog-container">
    <q-dialog
      ref="contactDialog"
      v-cloak
      :full-width="windowWidth <= 1025"
      :position="windowWidth <= 1025 ? 'bottom' : 'standard'"
      class="--fullscreen-height__padded"
    >
      <q-card class="bg-background-light" style="width: 1000px;">
        <q-btn
          round
          dense
          icon="sym_r_close"
          :ripple="false"
          v-close-popup
          class="--icon-button --bordered --shadow__bottom"
          color="background-light"
          style="position: absolute; top: 16px; right: 16px; z-index: 1000"
        />
        <section class="contact">
          <div class="contact__container row justify-center">
            <transition appear name="fade" mode="out-in">
              <div v-if="submitted" class="col-12 col-md-8">
                <div class="contact__form">
                  <div class="column items-start justify-start no-wrap">
                    <div class="q-mt-md">
                      <div class="flex items-center justify-start">
                        <h4 class="q-mb-none">Thanks for reaching out!</h4>
                        <q-icon
                          name="sym_r_done_outline"
                          size="40px"
                          class="text-positive q-ml-md"
                        />
                      </div>
                      <div class="q-mt-md text-left">
                        Our team will review your submission, and may contact
                        you if follow-up is required. <br /><br />Now, let's
                        continue your personalized car search!
                      </div>
                    </div>
                  </div>
                  <q-btn
                    color="background-light"
                    label="Take Me Home"
                    no-caps
                    to="/"
                    class="full-width q-mt-lg"
                  />
                </div>
              </div>
              <div v-else class="col-12 col-md-8">
                <q-form
                  class="full-width text-left contact__form"
                  autocorrect="off"
                  autocapitalize="off"
                  autocomplete="off"
                  spellcheck="false"
                  @submit="
                    () => {
                      submitContactForm();
                    }
                  "
                >
                  <div class="q-mb-lg">
                    <h3 class="q-mb-md">Get in touch with us.</h3>
                    <p class="--text-md">
                      Questions, concerns, or feedback? Let us know! Your
                      input is crucial in shaping the future of CarScience.
                    </p>
                  </div>
                  <div class="row q-col-gutter-x-lg q-col-gutter-y-sm q-mt-md">
                    <q-input
                      standout="text-black"
                      v-model="form.firstName"
                      label="First Name"
                      class="col-6"
                      lazy-rules
                      :rules="[
                        val =>
                          (val && val.length > 1) || 'Enter your first name.'
                      ]"
                      :disable="submitting"
                    />
                    <q-input
                      standout="text-black"
                      v-model="form.lastName"
                      label="Last Name"
                      class="col-6"
                      lazy-rules
                      :rules="[
                        val =>
                          (val && val.length > 1) || 'Enter your last name.'
                      ]"
                      :disable="submitting"
                    />
                    <q-input
                      standout="text-black"
                      v-model="form.email"
                      label="Email"
                      class="col-12 col-md-6"
                      lazy-rules
                      :rules="[
                        val =>
                          (val &&
                            val.length > 0 &&
                            val.toLowerCase().match(/^\S+@\S+\.\S+$/)) ||
                          'Enter your email address.'
                      ]"
                      :disable="submitting"
                    />
                    <q-input
                      standout="text-black"
                      v-model="form.phone"
                      label="Phone (Optional)"
                      class="col-12 col-md-6"
                      inputmode="numeric"
                      lazy-rules
                      :rules="[
                        val =>
                          !val ||
                          val.length == 0 ||
                          (val && val.length === 10) ||
                          'Enter your phone number.'
                      ]"
                      mask="(###) ###-####"
                      unmasked-value
                      :disable="submitting"
                    />
                    <q-input
                      type="textarea"
                      standout="text-black"
                      v-model="form.message"
                      label="Your Message..."
                      class="col-12"
                      lazy-rules
                      :rules="[
                        val => !!val || 'Enter your message.',
                        val =>
                          (val && val.length >= 20) ||
                          'Your message must be at least 20 characters.'
                      ]"
                      :disable="submitting"
                    />
                    <div class="col-12">
                      <div class="flex items-center justify-between">
                        <div />
                        <q-btn
                          rounded
                          label="Submit"
                          type="submit"
                          color="secondary"
                          no-caps
                          style="width: 180px"
                          :class="{ 'full-width': windowWidth <= 1025 }"
                          :disable="submitting"
                          :loading="submitting"
                        />
                      </div>
                    </div>
                  </div>
                </q-form>
              </div>
            </transition>
            <div class="contact__side-panel col-12 col-md-4">
              <div class="q-mb-lg">
                <h5 class="q-mb-sm">Need Support?</h5>
                <div class="text-text-light q-mr-lg">
                  Our support team is available around the clock to address any
                  concerns or queries you may have. Chat with a representative
                  now, or reach out to our team using one of the contact methods
                  available.
                </div>
              </div>
              <div class="contact__side-panel__content q-gutter-y-sm">
                <div>
                  <div class="flex items-center q-mb-sm">
                    <q-icon
                      name="sym_r_place"
                      color="secondary"
                      class="q-mr-sm"
                      style="font-size: 1.4rem"
                    />
                    <h6 class="q-mb-none">
                      Mailing Address
                    </h6>
                  </div>
                  <p class="text-text-light">
                    980 N Michigan Ave Ste 1090<br />
                    PMB 610075<br />
                    Chicago, IL 60611-4521<br /><br />
                  </p>
                </div>

                <div class="q-mb-md">
                  <div class="flex items-center q-mb-sm">
                    <q-icon
                      name="sym_r_support_agent"
                      color="secondary"
                      class="q-mr-sm"
                      style="font-size: 1.4rem"
                    />
                    <h6 class="q-mb-none">
                      Chat with Support
                    </h6>
                  </div>
                  <p class="text-text-light">
                    <a
                      href="mailto:support@carscience.com"
                      class="text-text-light"
                      >support@carscience.com</a
                    ><br />
                    <a href="tel:+18449012239" class="text-text-light"
                      >+1 (844) 901-2239</a
                    >
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ContactDialog",
  components: {},
  data() {
    return {
      submitted:
        window.localStorage.getItem("carscience-contact-submitted") == 1,
      submitting: false,
      form: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: ""
      }
    };
  },
  methods: {
    submitContactForm() {
      this.submitting = true;

      setTimeout(() => {
        axios({
          url:
            "https://api-v2.jumpfeed.com/internal/send-carscience-contact-email",
          method: "POST",
          data: {
            key: "a7273sd633_fn209wknr290kwe284_m784r",
            contact: this.form
          }
        })
          .then(() => {
            document.body.scrollTop = 0; // For Safari
            document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
            this.submitted = true;
            window.localStorage.setItem("carscience-contact-submitted", "1");
          })
          .catch(error => {
            console.log(error);
            this.triggerActionOutcomeAlert(
              "We encountered an error while submitting your contact. Please try again later, or contact support."
            );
          })
          .finally(() => {
            this.submitting = false;
          });
      }, 500);
    },
    show() {
      this.$refs.contactDialog.show();
    },
    hide() {
      this.$refs.contactDialog.hide();
    }
  }
};
</script>

<style scoped lang="scss">
.contact {
  background: color(background);

  .contact__container {
    position: relative;
    z-index: 1;
    @include contain-content-width;
    max-width: 1300px;
    padding: $space-lg !important;

    .contact__form {
      border-right: 1px solid color(border);
      padding: $space-lg $space-xl;
      margin-right: $space-xl;

      @include screen-md {
        border: none;
        padding: 0;
        margin: 0;
        border-bottom: 1px solid color(border);
        padding-bottom: $space-xl;
      }
    }

    .contact__side-panel {
      padding: $space-lg 0;
      padding-left: $space-xl;

      @include screen-md {
        padding: 0;
        margin-top: $space-xl;
      }

      .contact__side-panel__header {
        margin-bottom: $space-xl;
        @include screen-md {
          margin-bottom: $space-lg;
        }
      }

      .contact__side-panel__content {
        margin-top: $space-xl;
        @include screen-md {
          margin-top: $space-lg;
        }
      }
    }
  }
}
</style>
<style lang="scss">
input {
  -webkit-background-clip: text !important;
}

input:-webkit-autofill {
  -webkit-text-fill-color: white;
}
input:-webkit-autofill:focus {
  -webkit-text-fill-color: #000;
}
</style>
